/**
 * Per-pane history (1.I-2 deliverable 2): a pure stack of the pages one pane has shown, with its
 * own back and forward. A push drops the forward entries and keeps at most `HISTORY_CAP`.
 */
export interface NavEntry {
  readonly docId: string;
  readonly blockId?: string | undefined;
}

export interface PaneStack {
  readonly entries: readonly NavEntry[];
  readonly index: number;
}

export const HISTORY_CAP = 50;

export function openStack(entry: NavEntry): PaneStack {
  return { entries: [entry], index: 0 };
}

export function currentEntry(stack: PaneStack): NavEntry {
  return stack.entries[stack.index] as NavEntry;
}

function same(a: NavEntry, b: NavEntry): boolean {
  return a.docId === b.docId && a.blockId === b.blockId;
}

/** Shows `entry` next; the page already shown is not pushed again. */
export function push(stack: PaneStack, entry: NavEntry): PaneStack {
  if (same(currentEntry(stack), entry)) return stack;
  const kept = [...stack.entries.slice(0, stack.index + 1), entry];
  const trimmed = kept.length > HISTORY_CAP ? kept.slice(kept.length - HISTORY_CAP) : kept;
  return { entries: trimmed, index: trimmed.length - 1 };
}

export function canBack(stack: PaneStack): boolean {
  return stack.index > 0;
}

export function canForward(stack: PaneStack): boolean {
  return stack.index < stack.entries.length - 1;
}

export function back(stack: PaneStack): PaneStack {
  return canBack(stack) ? { ...stack, index: stack.index - 1 } : stack;
}

export function forward(stack: PaneStack): PaneStack {
  return canForward(stack) ? { ...stack, index: stack.index + 1 } : stack;
}
